/**
 * InstallAppButton — Prominent CTA for PWA installation.
 *
 * Render logic:
 *   isStandalone    → null (already installed, no banner needed)
 *   canInstallDirect → calls the native browser install prompt
 *   isIOS           → opens InstallAppDialog (variant 'ios', Safari share-sheet steps)
 *   otherwise       → opens InstallAppDialog (variant 'fallback', browser-menu steps)
 *
 * Accepts an optional `sx` prop to let the parent control outer spacing.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Box } from '@mui/material';
import { GetApp } from '@mui/icons-material';
import { motion } from 'framer-motion';
import usePwaInstall from '../hooks/usePwaInstall';
import InstallAppDialog from './InstallAppDialog';
import { GRADIENTS, RADIUS_LIBRARIAN as RADIUS } from '../theme/tokens';

const MotionBox = motion(Box);

function InstallAppButton({ sx }) {
  const { canInstallDirect, isIOS, isStandalone, promptInstall } = usePwaInstall();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogVariant, setDialogVariant] = useState('fallback');

  // Already installed as a PWA — render nothing
  if (isStandalone) return null;

  function openDialog(variant) {
    setDialogVariant(variant);
    setDialogOpen(true);
  }

  async function handleClick() {
    if (canInstallDirect) {
      const result = await promptInstall();
      // If the prompt was already consumed (race condition), fall back to dialog
      if (result.outcome === 'unavailable') {
        openDialog('fallback');
      }
      return;
    }

    if (isIOS) {
      openDialog('ios');
      return;
    }

    openDialog('fallback');
  }

  return (
    <>
      <MotionBox
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        sx={sx}
      >
        <Button
          variant="contained"
          startIcon={<GetApp />}
          onClick={handleClick}
          fullWidth
          aria-label="Install BookMyLibrary app"
          sx={{
            background: GRADIENTS.brand,
            color: 'white',
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: RADIUS.lg,
            fontSize: '0.875rem',
            py: 1.25,
            boxShadow: '0 4px 14px rgba(242,101,34,0.35)',
            '&:hover': {
              background: GRADIENTS.brandDeep,
              boxShadow: '0 6px 18px rgba(242,101,34,0.45)',
            },
          }}
        >
          Install App
        </Button>
      </MotionBox>

      <InstallAppDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        variant={dialogVariant}
      />
    </>
  );
}

InstallAppButton.propTypes = {
  /** Optional MUI sx overrides — use for outer margin/padding from the parent */
  sx: PropTypes.object,
};

InstallAppButton.defaultProps = {
  sx: undefined,
};

export default InstallAppButton;
